import set from 'lodash/fp/set';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

export type JsonPrimitive = string | number | boolean | null;

export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export interface JsonObject {
  [key: string]: JsonValue;
}

export const isJsonObject = (value: JsonValue | undefined): value is JsonObject =>
  !isNil(value) && typeof value === 'object' && !Array.isArray(value);

export const parseJsonStringArray = (value: string): string[] => {
  try {
    const parsed: JsonValue = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.reduce((acc: string[], item) => {
      if (isString(item) && !isEmpty(item)) {
        acc.push(item);
      }
      return acc;
    }, []);
  } catch {
    return [];
  }
};

export const readPath = (root: JsonObject, path: string): JsonValue | undefined => get(root, path);

export const readObject = (root: JsonObject, path: string): JsonObject => {
  const value = readPath(root, path);
  return isJsonObject(value) ? value : {};
};

export const readString = (root: JsonObject, path: string): string | null => {
  const value = readPath(root, path);
  return isString(value) ? value : null;
};

export const readNumber = (root: JsonObject, path: string): number | null => {
  const value = readPath(root, path);
  return isNumber(value) ? value : null;
};

export const readBoolean = (root: JsonObject, path: string): boolean => readPath(root, path) === true;

export const readStringList = (root: JsonObject, path: string): string[] => {
  const value = readPath(root, path);
  if (isString(value)) {
    return [value];
  }
  return Array.isArray(value) ? value.filter(isString) : [];
};

export const readObjectList = (root: JsonObject, path: string): JsonObject[] => {
  const value = readPath(root, path);
  return Array.isArray(value) ? value.filter(isJsonObject) : [];
};

/** Immutable: returns a copy of the root with the value set at the dotted path. */
export const setPath = (root: JsonObject, path: string, value: JsonValue): JsonObject => set(path, value, root);
